import React from 'react';
import { motion } from 'framer-motion';
import {
    BookOpen,
    Search,
    Compass,
    Leaf,
    Sparkles,
    History,
    ChevronRight,
    ScrollText,
    ArrowRight,
    GraduationCap
} from 'lucide-react';
import mainlibrary from '../assets/libraryimg.jpeg';
import bookrac from '../assets/bookrac.jpeg';
import down from "../assets/down.png";
import up from "../assets/up.png";

export default function Library() {
    const darkGreen = "#1A2E20";
    const goldAccent = "#D4AF37";
    const creamBg = "#FDFBF7";
    const bgPattern = "https://www.transparenttextures.com/patterns/natural-paper.png";

    const fadeIn = {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-50px" },
        transition: { duration: 0.8, ease: "easeOut" }
    };

    const categories = [
        {
            title: "Holistic Medicine",
            count: "4500+ Volumes",
            description: "Books containing in-depth and extensive knowledge about Sinhala traditional medicine, Ayurveda, Western allopathic medicine and alternative healing methods.",
            icon: <Leaf size={28} className="text-[#D4AF37]" />,
            image: "https://images.unsplash.com/photo-1512290923902-8a9f81dc236c?auto=format&fit=crop&w=800&q=80"
        },
        {
            title: "Buddhism and Spirituality",
            count: "4000+ Volumes",
            description: "Books related to Buddhist teachings and other spiritual subjects aimed at the liberation of all beings.",
            icon: <Sparkles size={28} className="text-[#D4AF37]" />,
            image: "https://images.unsplash.com/photo-1542353436-312f0e1f67ff?auto=format&fit=crop&w=800&q=80"
        },
        {
            title: "Ancient Wisdom",
            count: "3000+ Volumes",
            description: "Knowledge systems that must be preserved, inherited from ancient traditions that exist outside the scientific method.",
            icon: <ScrollText size={28} className="text-[#D4AF37]" />,
            image: "https://images.unsplash.com/photo-1457369804613-52c61a468e7d?auto=format&fit=crop&w=800&q=80"
        },
        {
            title: "Literature",
            count: "5000+ Volumes",
            description: "Literary books that provide happiness through enjoyment without weakening people's minds emotionally and sentimentally.",
            icon: <BookOpen size={28} className="text-[#D4AF37]" />,
            image: "https://images.unsplash.com/photo-1516979187457-637abb4f9353?auto=format&fit=crop&w=800&q=80"
        },
        {
            title: "Academic Books",
            count: "3500+ Volumes",
            description: "Books written on various subject areas that allow students to learn abstract knowledge.",
            icon: <GraduationCap size={28} className="text-[#D4AF37]" />,
            image: "https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=800&q=80"
        },
        {
            title: "Personal Development",
            count: "2500+ Volumes",
            description: "Books written on subjects such as self-help, biographies, history, etc., focusing on living an advanced life.",
            icon: <Compass size={28} className="text-[#D4AF37]" />,
            image: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=800&q=80"
        }
    ];

    return (
        <div
            style={{ backgroundColor: creamBg, color: darkGreen }}
            className="min-h-screen font-sans selection:bg-[#D4AF37] selection:text-white relative overflow-hidden"
        >
            {/* PAPER TEXTURE OVERLAY */}
            <div className="absolute inset-0 pointer-events-none opacity-40 z-10" style={{ backgroundImage: `url(${bgPattern})` }}></div>

            {/* 1. HERO SECTION */}
            <section className="relative h-[60vh] md:h-[65vh] flex items-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img
                        src={mainlibrary}
                        className="w-full h-full object-cover"
                        alt="Library Interior"
                    />
                    <div className="absolute inset-0 bg-[#1A2E20]/60 backdrop-blur-[2px]"></div>
                </div>

                <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 w-full pt-20">
                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
                        <div className="flex items-center gap-2 text-white/90 text-[9px] sm:text-[10px] tracking-[0.3em] sm:tracking-[0.4em] uppercase mb-4 sm:mb-6 font-bold">
                            <span>Home</span>
                            <ChevronRight size={12} className="text-[#D4AF37]" />
                            <span className="text-white font-medium">Memorial Library</span>
                        </div>
                        <h1 className="font-serif text-4xl sm:text-6xl md:text-8xl text-white leading-tight">
                            D. Manike Ranathunga <br/>
                            <span className="italic font-light text-[#D4AF37]">Memorial Library</span>
                        </h1>
                        <p className="mt-6 sm:mt-8 text-white/80 text-xs sm:text-base md:text-xl font-light tracking-widest uppercase max-w-2xl">
                            Awaken Within. Learn Beyond. Read. Reflect. Rise.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* 2. SACRED SPACE DESCRIPTION */}
            <section className="relative py-16 sm:py-24 px-4 sm:px-6 z-20">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div {...fadeIn}>
                        <div className="flex justify-center mb-4 sm:mb-6">
                            <img src={up} alt="Flourish Top" className="w-auto h-12 sm:h-16 md:h-20 object-contain" />
                        </div>
                        <span style={{ color: goldAccent }} className="font-bold uppercase tracking-[0.3em] sm:tracking-[0.4em] text-[10px] sm:text-[11px] block mb-4 sm:mb-6">A Sacred Sanctuary</span>
                        <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl text-[#1A2E20] mb-6 sm:mb-8 leading-tight">Devoted to Human Inner Mastery</h2>
                        <p className="text-gray-600 text-sm sm:text-base md:text-lg font-light leading-relaxed mb-8 sm:mb-10 max-w-3xl mx-auto">
                            This library is more than a collection of books; it is a nurturing ground for seekers, healers, and learners. Our curated selection inspires personal transformation and the awakening of a higher purpose through texts that have guided humanity for millennia.
                        </p>
                        <div className="flex justify-center mt-4 sm:mt-6">
                            <img src={down} alt="Divider Bottom" className="w-auto h-2 sm:h-3 md:h-4 object-contain" />
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* 3. COLLECTION CATEGORIES (Grid Style) */}
            <section className="relative pb-20 sm:pb-32 px-4 sm:px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-end justify-between mb-12 sm:mb-16 border-b border-[#1A2E20]/10 pb-6 sm:pb-8">
                        <div className="text-center sm:text-left mx-auto sm:mx-0">
                            <span style={{ color: goldAccent }} className="font-bold uppercase tracking-[0.3em] sm:tracking-[0.4em] text-[10px] sm:text-[11px]">What's on our shelves?</span>
                            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#1A2E20] mt-2">Curated Collections</h2>
                        </div>
                        <div className="hidden md:flex items-center gap-4 text-[#1A2E20]/40">
                            <Search size={20} />
                            <span className="text-[10px] uppercase tracking-widest font-bold">Search the Archives</span>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 sm:gap-10">
                        {categories.map((cat, i) => (
                            <motion.div
                                key={i}
                                {...fadeIn}
                                transition={{ delay: i * 0.1 }}
                                className="group flex flex-col lg:flex-row bg-white rounded-[1.5rem] sm:rounded-[2rem] overflow-hidden shadow-xl border border-gray-100 max-w-[340px] sm:max-w-full mx-auto w-full"
                            >
                                <div className="lg:w-2/5 relative overflow-hidden aspect-[4/3] lg:aspect-auto">
                                    <img
                                        src={cat.image}
                                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                                        alt={cat.title}
                                    />
                                    <div className="absolute inset-0 bg-[#1A2E20]/20"></div>
                                </div>
                                <div className="p-6 sm:p-10 lg:w-3/5 flex flex-col justify-center text-center sm:text-left items-center sm:items-start">
                                    <div className="mb-3 sm:mb-4">{cat.icon}</div>
                                    <h3 className="font-serif text-xl sm:text-2xl text-[#1A2E20] mb-1 sm:mb-2">{cat.title}</h3>
                                    <p style={{ color: goldAccent }} className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest mb-3 sm:mb-4">{cat.count}</p>
                                    <p className="text-gray-500 text-xs sm:text-sm md:text-base font-light leading-relaxed">
                                        {cat.description}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 4. VISITING INFO */}
            <section className="relative py-16 sm:py-24 bg-[#1A2E20] text-white px-4 sm:px-6">
                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                        <motion.div {...fadeIn} className="text-center lg:text-left">
                            <h2 className="font-serif text-3xl sm:text-5xl mb-8 leading-tight">Accessing <br/> <span className="italic font-light text-[#D4AF37]">Ancient Knowledge</span></h2>
                            <div className="space-y-6 sm:space-y-8 text-left">
                                <div className="flex gap-4 sm:gap-6">
                                    <div className="h-9 w-9 sm:h-10 sm:w-10 shrink-0 rounded-full border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] font-serif italic text-lg sm:text-xl">1</div>
                                    <div>
                                        <h4 className="font-serif text-lg sm:text-xl mb-1 sm:mb-2 text-[#D4AF37]">Public Reference</h4>
                                        <p className="text-gray-400 text-xs sm:text-sm font-light">The library is open for public reference and quiet study. Visitors are encouraged to engage with texts in our reading garden.</p>
                                    </div>
                                </div>
                                <div className="flex gap-4 sm:gap-6">
                                    <div className="h-9 w-9 sm:h-10 sm:w-10 shrink-0 rounded-full border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] font-serif italic text-lg sm:text-xl">2</div>
                                    <div>
                                        <h4 className="font-serif text-lg sm:text-xl mb-1 sm:mb-2 text-[#D4AF37]">Access for researchers</h4>
                                        <p className="text-gray-400 text-xs sm:text-sm font-light">Access for institutional and non-institutional researchers who have an interest and need for academic work.</p>
                                    </div>
                                </div>
                                <div className="flex gap-4 sm:gap-6">
                                    <div className="h-9 w-9 sm:h-10 sm:w-10 shrink-0 rounded-full border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] font-serif italic text-lg sm:text-xl">3</div>
                                    <div>
                                        <h4 className="font-serif text-lg sm:text-xl mb-1 sm:mb-2 text-[#D4AF37]">Conservation</h4>
                                        <p className="text-gray-400 text-xs sm:text-sm font-light">A library is a separate structure for knowledge management. The preservation of traditional knowledge and ancient wisdom also falls under the responsibility of a library.</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div {...fadeIn} className="relative aspect-square max-w-[340px] sm:max-w-md lg:max-w-full mx-auto w-full">
                            <div className="absolute inset-0 border-2 border-[#D4AF37] translate-x-3 translate-y-3 sm:translate-x-4 sm:translate-y-4 rounded-[2rem] sm:rounded-[3rem]"></div>
                            <img
                                src={bookrac}
                                className="relative z-10 w-full h-full object-cover rounded-[2rem] sm:rounded-[3rem] shadow-2xl"
                                alt="Old books"
                            />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* 5. CALL TO ACTION */}
            <section className="relative py-16 sm:py-24 px-4 sm:px-6">
                <div className="max-w-6xl mx-auto">
                    <motion.div
                        {...fadeIn}
                        className="bg-white border border-[#E5E0D8] rounded-[2rem] sm:rounded-[3rem] p-6 sm:p-12 md:p-20 text-center shadow-2xl relative overflow-hidden"
                    >
                        {/* Decorative Background Pattern */}
                        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: `url(${bgPattern})` }}></div>

                        <div className="relative z-10">
                            <BookOpen color={goldAccent} size={36} className="mx-auto mb-6 sm:mb-8 sm:w-10 sm:h-10" />
                            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#1A2E20] mb-4 sm:mb-6">Contribute to the Collection</h2>
                            <p className="text-gray-600 text-sm sm:text-base md:text-lg font-light max-w-2xl mx-auto mb-8 sm:mb-10 leading-relaxed">
                                Help us grow our heritage. We accept donations of books related to traditional medicine, philosophy, and spiritual growth.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
                                <button className="w-full sm:w-auto bg-[#1A2E20] text-white px-8 sm:px-10 py-4 sm:py-5 rounded-full font-bold uppercase text-[11px] sm:text-[12px] tracking-[0.2em] hover:bg-[#D4AF37] transition-all flex items-center justify-center gap-3 group shadow-lg">
                                    Donate a Book <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                                </button>
                                <button className="w-full sm:w-auto bg-transparent border-2 border-[#1A2E20] text-[#1A2E20] px-8 sm:px-10 py-4 sm:py-5 rounded-full font-bold uppercase text-[11px] sm:text-[12px] tracking-[0.2em] hover:bg-[#1A2E20] hover:text-white transition-all duration-300">
                                    Volunteer as Archivist
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}